<template>
  <li
    :id="`${menuId}-${node.uid}`"
    role="menuitem"
    :aria-haspopup="!isLeaf"
    :aria-owns="isLeaf ? null : menuId"
    :aria-expanded="inExpandingPath"
    :tabindex="expandable || canExpandDisabled ? -1 : undefined"
    :class="[
      ns.b(),
      ns.is('selectable', checkStrictly),
      ns.is('active', node.checked),
      ns.is('last-active', lastCheckedPath),
      ns.is('disabled', !expandable),
      inExpandingPath && 'in-active-path',
      inCheckedPath && 'in-checked-path',
      panel?.config?.panelWidth && ns.is('node-height'),
    ]"
    @mouseenter="handleHoverExpand"
    @focus="handleHoverExpand"
    @click="handleClick"
  >
    <!-- prefix -->
    <jd-checkbox
      v-if="multiple"
      :class="ns.is('full-width', isLeaf)"
      :model-value="node.checked"
      :indeterminate="node.indeterminate"
      :disabled="isDisabled"
      @click.stop
      @update:model-value="handleSelectCheck"
    >
      <node-content v-if="isLeaf" :node="node" :panel="panel" />
    </jd-checkbox>
    <jd-radio
      v-else-if="checkStrictly"
      :model-value="checkedNodeId"
      :label="node.uid"
      :disabled="isDisabled"
      @update:model-value="handleSelectCheck"
      @click.stop
    >
      <!--
        Add an empty element to avoid render label,
        do not use empty fragment here for https://github.com/vuejs/vue-next/pull/2485
      -->
      <span />
    </jd-radio>

    <!-- content -->
    <node-content v-if="!(multiple && isLeaf)" :node="node" :panel="panel" />

    <!-- postfix -->
    <template v-if="!isLeaf">
      <jd-icon v-if="node.loading" :class="[ns.is('loading'), ns.e('postfix')]">
        <loading />
      </jd-icon>
      <jd-icon v-else :class="['arrow-right', ns.e('postfix')]">
        <arrow-right />
      </jd-icon>
    </template>
  </li>
</template>

<script lang="ts">
// @ts-nocheck
import { computed, defineComponent, inject } from 'vue'
import { AngleRight as ArrowRight, Loading } from '@jd/jdesign-icons-vue'
import JdCheckbox from '@jdesign-vue/components/checkbox'
import JdRadio from '@jdesign-vue/components/radio'
import JdIcon from '@jdesign-vue/components/icon'
import { useNamespace } from '@jdesign-vue/hooks'
import NodeContent from './node-content'
import { CASCADER_PANEL_INJECTION_KEY } from './types'
import type { default as CascaderNode } from './node'

import type { PropType } from 'vue'

export default defineComponent({
  name: 'JdCascaderNode',

  components: {
    JdCheckbox,
    JdRadio,
    NodeContent,
    JdIcon,
    Loading,
    ArrowRight,
  },

  props: {
    node: {
      type: Object as PropType<CascaderNode>,
      required: true,
    },
    menuId: String,
  },

  emits: ['expand'],

  setup(props, { emit }) {
    const panel = inject(CASCADER_PANEL_INJECTION_KEY)!

    const ns = useNamespace('cascader-node')
    const isHoverMenu = computed(() => panel.isHoverMenu)
    const multiple = computed(() => panel.config.multiple)
    const checkStrictly = computed(() => panel.config.checkStrictly)
    const checkedNodeId = computed(() => panel.checkedNodes[0]?.uid)
    const isDisabled = computed(() => {
      // 原有的禁用逻辑
      if (props.node.isDisabled) return true

      // multipleLimit禁用逻辑：当达到限制且当前节点未选中时禁用
      if (multiple.value && panel.multipleLimit > 0) {
        const currentCheckedCount = panel.checkedNodes.length
        const isAtLimit = currentCheckedCount >= panel.multipleLimit
        const isNodeChecked = props.node.checked

        // 如果达到限制且当前节点未选中，则禁用
        if (isAtLimit && !isNodeChecked) {
          return true
        }
      }

      return false
    })
    const isLeaf = computed(() => props.node.isLeaf)
    const expandable = computed(
      () =>
        ((checkStrictly.value || canExpandDisabled) && !isLeaf.value) ||
        !isDisabled.value
    )
    const canExpandDisabled = computed(
      () => isDisabled.value && panel.expandDisabled
    )
    const inExpandingPath = computed(() => isInPath(panel.expandingNode!))
    // only useful in check-strictly mode
    const inCheckedPath = computed(
      () => checkStrictly.value && panel.checkedNodes.some(isInPath)
    )
    const lastCheckedPath = computed(
      () =>
        panel?.checkedNodes?.[panel?.checkedNodes?.length - 1]?.uid ===
        props.node?.uid
    )

    const isInPath = (node: CascaderNode) => {
      const { level, uid } = props.node
      return node?.pathNodes[level - 1]?.uid === uid
    }

    const doExpand = () => {
      if (inExpandingPath.value) return
      panel.expandNode(props.node)
    }

    const doCheck = (checked: boolean) => {
      const { node } = props
      if (checked === node.checked) return
      panel.handleCheckChange(node, checked)
    }

    const doLoad = () => {
      panel.lazyLoad(props.node, () => {
        if (!isLeaf.value) doExpand()
      })
    }

    const handleHoverExpand = (e: Event) => {
      if (!isHoverMenu.value) return
      handleExpand()
      !isLeaf.value && emit('expand', e)
    }

    const handleExpand = () => {
      const { node } = props
      // do not exclude leaf node because the menus expanded might have to reset
      const expand = expandable.value
        ? expandable.value
        : canExpandDisabled.value
      if (!expand || node.loading) return
      node.loaded ? doExpand() : doLoad()
    }

    const handleClick = () => {
      if (isHoverMenu.value && !isLeaf.value) return

      if (
        isLeaf.value &&
        !isDisabled.value &&
        !checkStrictly.value &&
        !multiple.value
      ) {
        handleCheck(true)
      } else {
        handleExpand()
      }
    }

    const handleSelectCheck = (checked: boolean) => {
      if (checkStrictly.value) {
        doCheck(checked)
        if (props.node.loaded) {
          doExpand()
        }
      } else {
        handleCheck(checked)
      }
    }

    const handleCheck = (checked: boolean) => {
      if (!props.node.loaded) {
        doLoad()
      } else {
        doCheck(checked)
        !checkStrictly.value && doExpand()
      }
    }

    return {
      panel,
      isHoverMenu,
      multiple,
      checkStrictly,
      checkedNodeId,
      isDisabled,
      isLeaf,
      expandable,
      canExpandDisabled,
      inExpandingPath,
      inCheckedPath,
      lastCheckedPath,
      ns,
      handleHoverExpand,
      handleExpand,
      handleClick,
      handleCheck,
      handleSelectCheck,
    }
  },
})
</script>
